import Vue from 'vue';
import { database } from "@/plugins/firebase";
import { BALANCES } from "@/constants/Balances";
import { InstructorsCollection } from './InstructorModule';
import _ from "lodash";
import { BalancesCollection } from "@/store/modules/BalanceModule";
import { CURRENT_SEASON_YEAR } from "@/store/seasonYear";
import { logActivity } from '@/services/ActivityLogService';
const TransactionsCollection = 'transactions' + CURRENT_SEASON_YEAR;
const OPERATION = {
    ADD: 'add',
    SUB: 'subtract'
};
const instructors = {};
const getBalance = async (collection, document, id) => {
    if (instructors[id]) {
        return instructors[id];
    }
    let balance = await document.get();
    let data = balance.data();
    if (!data) {
        throw 'BALANCE NOT FOUND';
    }
    instructors[id] = data;
    return data;
};
const updateBalance = async (collection, transaction, batch, operation) => {
    const id = operation === OPERATION.ADD
        ? transaction.data.to
        : transaction.data.from;
    const document = await database
        .collection(collection)
        .doc(id);
    const data = await getBalance(collection, document, id);
    if (operation === OPERATION.ADD) {
        data.balance += transaction.data.value;
    }
    else if (operation === OPERATION.SUB) {
        data.balance -= transaction.data.value;
    }
    batch.set(document, data);
};
export const TransactionModule = {
    namespaced: true,
    state: [],
    mutations: {
        store(state, transaction) {
            const x = state.find(x => x.id === transaction.id);
            if (x) {
                if (_.isEqual(x, transaction)) {
                    return;
                }
                state.splice(state.indexOf(x), 1, transaction);
                return;
            }
            state.push(transaction);
        },
        update(state, transaction) {
            state.find((oldTransaction, index) => {
                if (oldTransaction.id === transaction.id) {
                    Vue.set(state, index, transaction);
                }
            });
        },
        clear(state) {
            state.splice(0, state.length);
        },
    },
    actions: {
        async store(context, transactions) {
            let batch = database.batch();
            if (!Array.isArray(transactions)) {
                transactions = [transactions];
            }
            for (let transaction of transactions) {
                if (transaction.data.value === 0) {
                    continue;
                }
                if (transaction.data.from !== 'cash') {
                    if ([BALANCES.EXTERNAL_BALANCE, BALANCES.MAIN_BALANCE, BALANCES.TAX_BALANCE].includes(transaction.data.from)) {
                        await updateBalance(BalancesCollection, transaction, batch, OPERATION.SUB);
                    }
                    else {
                        await updateBalance(InstructorsCollection, transaction, batch, OPERATION.SUB);
                    }
                }
                if (transaction.data.to !== 'cash') {
                    if ([BALANCES.EXTERNAL_BALANCE, BALANCES.MAIN_BALANCE, BALANCES.TAX_BALANCE].includes(transaction.data.to)) {
                        await updateBalance(BalancesCollection, transaction, batch, OPERATION.ADD);
                    }
                    else {
                        await updateBalance(InstructorsCollection, transaction, batch, OPERATION.ADD);
                    }
                }
                batch.set(database.collection(TransactionsCollection).doc(), transaction.data);
            }
            await batch.commit();
            // Log activity for each transaction (fire-and-forget)
            if (Array.isArray(transactions)) {
                for (const transaction of transactions) {
                    if (transaction.data.value !== 0) {
                        logActivity('CREATE', 'transaction', transaction.id || 'new', null, transaction.data);
                    }
                }
            }
        },
        async listen(context) {
            context.commit('clear');
            //await context.dispatch('loadCache');
            return database.collection(TransactionsCollection).onSnapshot(snapshot => {
                for (let change of snapshot.docChanges()) {
                    switch (change.type) {
                        case 'added':
                            context.commit('store', { id: change.doc.id, data: change.doc.data() });
                            break;
                        case 'modified':
                            context.commit('update', { id: change.doc.id, data: change.doc.data() });
                            context.dispatch('storeCache');
                            break;
                    }
                }
            });
        },
        async listenSpecific(context, userId) {
            context.commit('clear');
            //await context.dispatch('loadCache', userId);
            const onSnapshot = (snapshot) => {
                for (let change of snapshot.docChanges()) {
                    switch (change.type) {
                        case 'added':
                            context.commit('store', { id: change.doc.id, data: change.doc.data() });
                            context.dispatch('storeCache', userId);
                            break;
                        case 'modified':
                            context.commit('update', { id: change.doc.id, data: change.doc.data() });
                            context.dispatch('storeCache', userId);
                            break;
                    }
                }
            };
            return [
                database
                    .collection(TransactionsCollection)
                    .where('from', '==', userId)
                    .onSnapshot(onSnapshot),
                database
                    .collection(TransactionsCollection)
                    .where('to', '==', userId)
                    .onSnapshot(onSnapshot),
            ];
        },
        async storeCache(context, userId = '') {
            localStorage.removeItem(TransactionsCollection + userId);
            try {
                localStorage.setItem(TransactionsCollection + userId, JSON.stringify(context.state));
            }
            catch (e) { }
        },
        async loadCache(context, userId = '') {
            const cache = localStorage.getItem(TransactionsCollection + userId);
            if (!cache) {
                return;
            }
            const transactions = JSON.parse(cache);
            for (const transaction of transactions) {
                context.commit('store', { id: transaction.id, data: transaction.data });
            }
        },
    },
    getters: {
        // Active entities only (for selection dropdowns) - deletedAt is null, undefined, or missing
        active(state) {
            return state.filter(t => !t.data.deletedAt);
        },
        // All entities including deleted (for historical views)
        all(state) {
            return state;
        },
        // Deleted entities only (for admin filter)
        deleted(state) {
            return state.filter(t => !!t.data.deletedAt);
        },
        // Active first, then deleted (for management list per FR-010)
        allSortedForManagement(state) {
            const active = state.filter(t => !t.data.deletedAt);
            const deleted = state.filter(t => !!t.data.deletedAt);
            return [...active, ...deleted];
        },
        byUser(state) {
            return (userId) => state.filter(transaction => [transaction.data.from, transaction.data.to].includes(userId));
        },
        async fetchAll(state) {
            const docs = await database.collection(TransactionsCollection).get();
            const ret = [];
            docs.forEach(doc => {
                ret.push({
                    id: doc.id,
                    data: doc.data(),
                });
            });
            return ret;
        },
    }
};
